import React, { useEffect, useMemo, useState } from 'react';
import { UseQueryResult } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { IEmptyStateView, IFitoListProps } from '~/Components';
import { TableDefinition } from '~/Components/DataTable/DataTable.types';
import { IDragHistoryItem, useBoolean, useDebouncedSearch } from '~/Hooks';
import { IErrorResponse } from '~/Models/DTO/Error.models';
import { IMenuAction, IMenuBulkAction } from '~/Models/interfaces/menuAction.models';
import { selectErrors, selectLoading } from '~/store/app';
import { IListAction } from '../ListToolbar/ListAction.models';
import { IFitoList, IFitoListListActions, IFitoListSettings, IFitoListTab } from './interfaces';
import { SelectChangeEvent } from '@mui/material';
import {
  useFitoListDesktop,
  useFitoListDnD,
  useFitoListFiltersTabsSearch,
  useFitoListReactQueryPagination,
} from './FitoListReactQueryHooks';

type IQuery = { [key: string]: string | number | string[] | boolean | void | null };
export interface IFitoListUncontrolledProps<T> {
  title?: string;
  keyOfId: keyof T;
  listActions: IFitoListListActions;
  placeholder?: IEmptyStateView;
  tabs?: IFitoListTab[];
  setItemValues: (item: T) => IFitoList;
  actions?: IMenuAction[];
  getItemActions?: (item: T) => IMenuAction[];
  bulkActions?: IMenuBulkAction[];
  settings: IFitoListSettings;
  onRearrange?: (historyItem: IDragHistoryItem[], extraData?: any) => void;
  listReduxSelectorString: (state: any) => T[];
  pageSize?: number;
  onSelect?: (id: string | string[], checked?: boolean) => void;
  size?: 'small';
  tableDefinition?: TableDefinition;
  appliedFilters?: { [key: string]: string | string[] };
  filterComponent?: React.ReactNode;
  defaultSelectedIds?: string[];
  backendSearch?: (
    searchString: string,
    additionalQuery?: IQuery,
  ) => UseQueryResult<T[], IErrorResponse>;
  sortOptions?: { key: string; label: string }[];
  searchTextPlaceholder?: string;
}

const isTheListLoading = (
  isLoading: boolean,
  forceRefresh: boolean,
  isWaitingForRearrangeToEnd: boolean,
  searchResponseIsLoading: boolean | undefined,
) => isLoading || forceRefresh || isWaitingForRearrangeToEnd || !!searchResponseIsLoading;

const hasError = (
  error: IErrorResponse | null,
  searchResponseError: IErrorResponse | null | undefined,
) => error || searchResponseError || undefined;

interface IFormatQueryHelper {
  defaultQuery?: IQuery;
  appliedFilters?: IQuery;
  settings: IFitoListSettings;
  tabs?: IFitoListTab[];
  selectedTabIndex: number;
  sortFieldSelected: string;
  page: { size: number; current: number };
}

const formatQueryHelper = ({
  defaultQuery = {},
  appliedFilters = {},
  settings,
  tabs,
  selectedTabIndex,
  sortFieldSelected,
  page,
}: IFormatQueryHelper) => {
  let query = { ...defaultQuery, ...appliedFilters };

  if (settings.pagination) {
    if (settings.paginationSettings) {
      const { pageField, sizeField } = settings.paginationSettings;
      query = { ...query, [sizeField]: page.size, [pageField]: page.current };
    } else {
      query = { ...query, pageSize: page.size, page: page.current };
    }
  }
  if (tabs) {
    query = { ...query, ...tabs[selectedTabIndex]?.query };
  }
  if (sortFieldSelected) {
    query = { ...query, orderBy: sortFieldSelected };
  }

  return query;
};

export const useFitoListUncontrolled = <T extends object>({
  keyOfId,
  placeholder,
  appliedFilters,
  filterComponent,
  listActions,
  tabs,
  setItemValues,
  actions,
  getItemActions,
  bulkActions,
  settings,
  onRearrange,
  listReduxSelectorString,
  pageSize = 11,
  onSelect,
  tableDefinition,
  defaultSelectedIds = [],
  backendSearch,
  sortOptions,
  size,
  title,
  searchTextPlaceholder,
}: IFitoListUncontrolledProps<T>): IFitoListProps => {
  const dispatch = useDispatch();

  // state
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  const [isWaitingForRearrangeToEnd, setIsWaitingForRearrangeToEnd] = useState(false);
  const [selectedIds, setSelectedIds] = useState(defaultSelectedIds);
  const {
    state: forceRefresh,
    setTrue: enableForceRefresh,
    setFalse: unsetForceRefresh,
  } = useBoolean(true);
  const [searchText, setSearchText] = useState('');
  const [resetSearch, setResetSearch] = useState(false);
  const [sortFieldSelected, setSortFieldSelected] = useState('');

  // selectors
  const reduxList = useSelector(listReduxSelectorString);
  const isLoading = useSelector(selectLoading(listActions.actionName));
  const error = useSelector(selectErrors(listActions.actionName));

  // actions
  const loadList = bindActionCreators(listActions.load, dispatch);

  const tabsQuery = tabs?.[selectedTabIndex]?.query;
  const additionalBackendSearchQuery = { ...tabsQuery, ...appliedFilters };

  const searchResponse = backendSearch?.(searchText, additionalBackendSearchQuery);
  const unstructuredList = useMemo(() => (searchText ? searchResponse?.data : reduxList) || [], [
    searchText,
    searchResponse?.data,
    reduxList,
  ]);

  // hooks
  const { setInputText } = useDebouncedSearch((text: string) => {
    setSearchText(text);
    setPage(settings?.paginationSettings?.startAt ?? 1);
    setResetSearch(false);
  });

  const { onNextPage, onPreviousPage, page, setPage } = useFitoListReactQueryPagination({
    enableForceRefresh,
    pageSize,
    settings,
  });

  useEffect(() => {
    // reset search
    setSearchText('');
    setResetSearch(true);

    if (forceRefresh || appliedFilters) {
      const query = formatQueryHelper({
        defaultQuery: listActions.defaultQuery,
        appliedFilters,
        settings,
        page: {
          size: pageSize,
          current: page,
        },
        tabs,
        selectedTabIndex,
        sortFieldSelected,
      });

      loadList(query);
      unsetForceRefresh();
      setIsWaitingForRearrangeToEnd(false);
    }
  }, [forceRefresh, selectedTabIndex, page, appliedFilters, sortFieldSelected]);

  const {
    addToDnDHistory,
    isBeingRearranged,
    onRearrangeStart,
    setIsBeingRearranged,
  } = useFitoListDnD({
    enableForceRefresh,
    setIsWaitingForRearrangeToEnd,
    unstructuredList,
    onRearrange,
  });

  const { filteredList, onTabbedFilterChange, tabbedFilters } = useFitoListFiltersTabsSearch({
    enableForceRefresh,
    isBeingRearranged,
    keyOfId,
    searchText,
    setItemValues,
    setPage,
    setSelectedTabIndex,
    settings,
    unstructuredList,
    tabs,
    searchResponse,
    actions,
    getItemActions,
  });

  // actions
  const Actions: IListAction[] = useMemo(() => [], []);
  const BulkActions: IListAction[] = useMemo(
    () =>
      bulkActions?.map((action) => ({
        title: action.name,
        onClick: () => action.onClick({ ...listActions.defaultQuery, selectedIds }),
      })) || [],
    [bulkActions],
  );

  // selecting
  const OnSelect =
    onSelect &&
    ((id: string | string[], checked?: boolean | undefined) => {
      const listOfIds = typeof id === 'string' ? [id] : id;
      if (checked) {
        setSelectedIds([
          ...selectedIds,
          ...listOfIds?.filter((selectedId) => !selectedIds?.includes(selectedId.toString())),
        ]);
      } else {
        setSelectedIds(selectedIds?.filter((selectedId) => !id?.includes(selectedId.toString())));
      }
      onSelect(id, checked);
    });

  const items = useMemo(
    () =>
      filteredList
        .slice(0, pageSize - 1)
        .map((item) => ({ ...item, checked: !!selectedIds.includes(item.id) })),
    [filteredList, pageSize, selectedIds],
  );

  const { desktopView } = useFitoListDesktop({
    items,
    keyOfId,
    unstructuredList,
    tableDefinition,
  });

  const onSortSelect = (event: SelectChangeEvent) => {
    event.preventDefault();
    setSortFieldSelected(event.target.value);
  };

  return {
    items,
    tabbedFilters,
    onTabbedFilterChange,
    onSearch: settings.search ? setInputText : undefined,
    resetSearch,
    Actions,
    bulkActions: BulkActions,
    isLoading: isTheListLoading(
      isLoading,
      forceRefresh,
      isWaitingForRearrangeToEnd,
      searchResponse?.isLoading,
    ),
    onSelect: OnSelect,

    addToDnDHistory,
    onRearrange: (settings.rearrange || undefined) && onRearrange && onRearrangeStart,
    isBeingRearranged,
    setIsBeingRearranged,

    error: hasError(error, searchResponse?.error),
    errorAction: enableForceRefresh,

    filterComponent,
    pageSize: pageSize - 1,
    page: !settings.pagination || searchText ? undefined : page,
    onNextPage,
    onPreviousPage,
    pageStartValue: settings.paginationSettings?.startAt ?? 1,
    multiSelect: !!settings.multiSelect,
    isSelectionActive: !!selectedIds.length,
    placeholder,
    desktopView,
    sortOptions,
    onSortSelect,
    size,
    title,
    searchTextPlaceholder,
  };
};
